import { EventEmitter } from 'events';

import once from 'one-event';

import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';
import { NotAllowedError } from 'vbrick-player-src/MediaErrorCode';
import { userAgentUtil } from 'vbrick-player-src/UserAgentUtil';

import { VgStates } from 'vbrick-player-src/videogular/VgStates';

import { HlsPlayer } from './HlsPlayer';

export class HlsLiveResumeHelper extends EventEmitter {
	private readonly VIDEO_EVENT_PAUSE: string = 'pause';
	private readonly VIDEO_EVENT_PLAY: string = 'play';
	private readonly VIDEO_EVENT_PROGRESS: string = 'progress';
	private readonly VIDEO_EVENT_TIMEUPDATE: string = 'timeupdate';

	private onProgressChangeLivePositioningHandler: () => void;
	private onLivePauseHandler: () => void;
	private onLivePlayHandler: () => void;
	private onTimeupdateHandler: () => void;

	protected isLivePaused: boolean;

	public isResumeInProgress: boolean;

	constructor(protected hlsPlayer: HlsPlayer, protected API: VideogularComponent) {
		super();
	}

	public destroy(): void {
		if (this.hlsPlayer.isLive) {
			this.destroyLiveListeners();
		}

		this.hlsPlayer = this.API = null;

		super.removeAllListeners();
	}

	public initialize(): void {
		if (this.hlsPlayer.isLive) {
			this.isLivePaused = true;
			this.isResumeInProgress = false;

			this.initLiveListeners();

			if (this.hlsPlayer.isLive && this.hlsPlayer.isAutoPlay) {
				this.onLivePlay();
			}
		}
	}

	public destroyLiveListeners(): void {
		this.stopLiveResumePositioning();

		this.hlsPlayer?.videoElement?.removeEventListener(this.VIDEO_EVENT_PAUSE, this.onLivePauseHandler);
		this.hlsPlayer?.videoElement?.removeEventListener(this.VIDEO_EVENT_PLAY, this.onLivePlayHandler);
		this.hlsPlayer?.videoElement?.removeEventListener(this.VIDEO_EVENT_TIMEUPDATE, this.onTimeupdateHandler);
	}

	protected initLiveListeners(): void {
		this.onLivePauseHandler = () => this.onLivePause();
		this.onLivePlayHandler = () => this.onLivePlay();
		this.onTimeupdateHandler = () => this.onTimeUpdate();

		this.hlsPlayer?.videoElement?.addEventListener(this.VIDEO_EVENT_PAUSE, this.onLivePauseHandler);
		this.hlsPlayer?.videoElement?.addEventListener(this.VIDEO_EVENT_PLAY, this.onLivePlayHandler);
		this.hlsPlayer?.videoElement?.addEventListener(this.VIDEO_EVENT_TIMEUPDATE, this.onTimeupdateHandler);
	}

	protected isSeekable(position: number): boolean {
		const videoElement: HTMLVideoElement = this.hlsPlayer.videoElement;

		if (position > videoElement.duration) {
			return false;
		}

		const seekable: TimeRanges = videoElement.seekable;

		return Array.from((seekable as any) as Iterable<any>) // works fine, but TS doesn't recognize seekable as being iterable (typedef defect?)
			.reduce((result, _currentValue, currentIndex) =>
				result || (position >= seekable.start(currentIndex) && position < Math.floor(seekable.end(currentIndex))),
				false
			);
	}

	protected onLivePause(): void {
		//workaround for safari:-
		//When window is getting resized or tab is getting changed then
		//video element firing pause event and it's detaching the media.
		//We should only detach media if vgAPI state in stop state.
		if (this.API.currentState && this.API.currentState !== VgStates.STOP) {
			return;
		}
		this.stopLiveResumePositioning();

		if (this.hlsPlayer.isRecovering) {
			return;
		}

		// have hls.js stop loading data
		this.hlsPlayer.player.stopLoad();
		this.hlsPlayer.player.detachMedia();

		this.isLivePaused = true;
		this.isResumeInProgress = false;
	}

	protected onLivePlay(): void {
		if (!this.isLivePaused || this.hlsPlayer.isRecovering) {
			return;
		}

		this.isResumeInProgress = true;
		this.API.isBufferingOverride = true;

		this.hlsPlayer.pause(false);
		this.hlsPlayer.onLiveResume();

		// restart hls.js data loading
		this.hlsPlayer.streamController.lastCurrentTime = -1; // reset the internal hls.js position tracking to make loading smoother

		this.hlsPlayer.player.attachMedia(this.hlsPlayer.videoElement);
		this.hlsPlayer.player.startLoad();

		this.startLiveResumePositioning();

		this.isLivePaused = false;
	}

	protected toFixed(input: number): number {
		return +input.toFixed(3);
	}

	private onProgressChangeLivePositioning(): void {
		const rawLiveSyncPosition: number = this.hlsPlayer.player.liveSyncPosition;

		if (typeof rawLiveSyncPosition !== 'number') {
			return;
		}

		const videoElement: HTMLVideoElement = this.hlsPlayer.videoElement;
		const liveSyncPosition: number = this.toFixed(rawLiveSyncPosition);
		const isLiveSyncPositionSeekable: boolean = this.isSeekable(liveSyncPosition);

		if (isLiveSyncPositionSeekable) {
			this.stopLiveResumePositioning();

			if (this.toFixed(videoElement.currentTime) < liveSyncPosition) { // seek to the liveSyncPosition and resume playback once there
				once(this.hlsPlayer.videoElement, 'seeked')
					.then(() => this.resume());

				videoElement.currentTime = liveSyncPosition;

				// stall detection and recovery
				window.setTimeout(() => {
					if (videoElement.paused && videoElement.currentTime >= liveSyncPosition) {
						this.onPlay(videoElement);
					}
				}, 1000);

			} else if (this.toFixed(videoElement.currentTime) === liveSyncPosition || videoElement.paused) { // already where we need to be
				this.resume();
			}
		}
	}

	private resume(): void {
		this.hlsPlayer.play(false, true);

		this.isResumeInProgress = false;

		this.emit('resume');
	}

	//workaround to set isBufferingOverride = false
	//This is needed because on large ts segment, the initial spinner keeps showing even if playback is going on. (AV-27668)
	protected onTimeUpdate(): void {
		if (this.API?.isBufferingOverride && !this.hlsPlayer.videoElement.paused && this.hlsPlayer.videoElement.readyState === 4) {
			console.log('hiding initial spinner before progress event');
			this.API.isBufferingOverride = false;
		}
	}

	private startLiveResumePositioning(): void {
		this.onProgressChangeLivePositioningHandler = () => this.onProgressChangeLivePositioning();

		this.hlsPlayer.videoElement.addEventListener(this.VIDEO_EVENT_PROGRESS, this.onProgressChangeLivePositioningHandler);
	}

	private stopLiveResumePositioning(): void {
		this.API.isBufferingOverride = false;

		this.hlsPlayer.videoElement.removeEventListener(this.VIDEO_EVENT_PROGRESS, this.onProgressChangeLivePositioningHandler);
	}

	private onPlay(videoElement: HTMLVideoElement): void {
		const promise = videoElement.play();

		promise.catch(err => {
			if (err.name === NotAllowedError) {
				console.warn('HlsLiveResumeHelper onPlay method', err, err.name);
				setTimeout(() => {
					this.API?.resetVolumeIfNeeded?.(true);
					videoElement.play();
				});
			}
		});

	}
}
